import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface EditRemunerationGroupsParams {
  year: number;
  remunerationGroup: string;
  receivingPpo: string;
}

export interface ExchangeOffice {
  ppo: string;
  exchangeOffice: string;
  quarter1: boolean;
  quarter2: boolean;
  quarter3: boolean;
  quarter4: boolean;
  newPpo?: string;
  newExchangeOffice?: string; // This will store the name value (e.g., "BEBRUA", "ATHALA")
}

export interface EditRemunerationGroupRow {
  rgId: number;
  year: number;
  receivingPpo: string;
  exchangeOffices: ExchangeOffice[];
  notSubjToSampling: boolean;
}

export interface EditRemunerationGroupsResponse {
  data?: EditRemunerationGroupRow;
  url?: string;
  content?: Blob;
  filename?: string;
}

export interface RemunerationGroupOption {
  id: string;
  label: string;
}

export interface ExchangeOfficeOption {
  id: string; // This will be the name value (e.g., "BEBRUA", "ATHALA")
  label: string; // This will also be the name value for display
}

// Hook to fetch remuneration group options
export function useRemunerationGroupOptions() {
  return useQuery<RemunerationGroupOption[]>({
    queryKey: ['remuneration-group-options'],
    queryFn: async () => {
      const response = await axios.get('/upu-rg/all');
      
      // Transform the response data to match the expected format
      // Assuming the API returns an array of objects with id and name/displayName properties
      return response.data.map((item: any) => ({
        id: item.id || item.code,
        label: item.displayName || item.name || item.label,
      }));
    },
  });
}

// Hook to fetch exchange office options
export function useExchangeOfficeOptions() {
  return useQuery<ExchangeOfficeOption[]>({
    queryKey: ['exchange-office-options'],
    queryFn: async () => {
      const response = await axios.get('/impc', {
        params: {
          page: 0,
          size: 500,
          sort: 'name,asc',
        }
      });
      
      // Transform the response data to match the expected format
      // The API returns a paginated response with content array
      if (response.data?.content && Array.isArray(response.data.content)) {
        // Extract unique name values and create options
        const uniqueExchangeOffices = new Map<string, string>();
        
        response.data.content.forEach((item: any) => {
          if (item.name && !uniqueExchangeOffices.has(item.name)) {
            uniqueExchangeOffices.set(item.name, item.name);
          }
        });
        
        return Array.from(uniqueExchangeOffices.entries())
          .map(([name, label]) => ({
            id: name,
            label: name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
      
      return [];
    },
  });
}

// Hook to fetch exchange office options filtered by designated operator
export function useExchangeOfficeOptionsByDO(designatedOperator?: string) {
  return useQuery<ExchangeOfficeOption[]>({
    queryKey: ['exchange-office-options', designatedOperator],
    queryFn: async () => {
      if (!designatedOperator) {
        return [];
      }
      
      // Create URLSearchParams to handle query parameters
      const searchParams = new URLSearchParams();
      searchParams.append('designatedOperator', designatedOperator);
      
      const response = await axios.get(`/impc?${searchParams.toString()}`, {
        params: {
          page: 0,
          size: 500,
          sort: 'name,asc',
        }
      });
      
      // Transform the response data to match the expected format
      // The API returns a paginated response with content array
      if (response.data?.content && Array.isArray(response.data.content)) {
        // Extract unique name values and create options
        const uniqueExchangeOffices = new Map<string, string>();
        
        response.data.content.forEach((item: any) => {
          if (item.name && !uniqueExchangeOffices.has(item.name)) {
            uniqueExchangeOffices.set(item.name, item.name);
          }
        });
        
        return Array.from(uniqueExchangeOffices.entries())
          .map(([name, label]) => ({
            id: name,
            label: name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
      
      return [];
    },
    enabled: !!designatedOperator, // Only run query when designatedOperator is provided
  });
}

// Hook to fetch available sending PPOS for UPU based on receiving PPO
export function useAvailableSendingPpos(receivingPpo?: string) {
  return useQuery<RemunerationGroupOption[]>({
    queryKey: ['upu-available-sending-ppos', receivingPpo],
    queryFn: async () => {
      if (!receivingPpo) return [];

      const response = await axios.get('/upu-rg/available-sending-ppos', {
        params: { receivingPpo },
      });

      const data = response.data;
      if (Array.isArray(data)) {
        return data
          .map((item: any) => {
            if (typeof item === 'string') return { id: item, label: item };
            const id = String(item?.id ?? item?.code ?? item?.name ?? '');
            const label = String(item?.label ?? item?.displayName ?? item?.name ?? id);
            return { id, label };
          })
          .filter((o: RemunerationGroupOption) => o.id)
          .sort((a: RemunerationGroupOption, b: RemunerationGroupOption) => a.label.localeCompare(b.label));
      }
      return [];
    },
    enabled: !!receivingPpo,
  });
}

// Hook to search edit remuneration groups
export function useEditRemunerationGroups() {
  return useMutation<EditRemunerationGroupsResponse, Error, EditRemunerationGroupsParams>({
    mutationFn: async (params: EditRemunerationGroupsParams) => {
      // Create URLSearchParams to handle query parameters
      const searchParams = new URLSearchParams();
      
      // Add parameters
      searchParams.append('year', params.year.toString());
      searchParams.append('remunerationGroup', params.remunerationGroup);
      searchParams.append('receivingPpo', params.receivingPpo);

      const response = await axios.get(`/upu-rg/search?${searchParams.toString()}`, {
        responseType: 'json',
      });

      return { data: response.data };
    },
  });
}

// Hook to update remuneration group
export function useUpdateRemunerationGroup() {
  return useMutation<any, Error, { id: number; data: any }>({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`/upu-rg/${id}`, data);
      return response.data;
    },
  });
}

// Hook to save remuneration group fragments
export function useSaveRemunerationGroupFragments() {
  return useMutation<any, Error, any>({
    mutationFn: async (data) => {
      const response = await axios.post('/upu-rg', data);
      return response.data;
    },
  });
}

// Hook to save remuneration group fragments with confirmation disabled
export function useSaveRemunerationGroupFragmentsWithoutConfirmation() {
  return useMutation<any, Error, any>({
    mutationFn: async (data) => {
      const response = await axios.put('/upu-rg', data);
      return response.data;
    },
  });
} 